'use client';

import { useEffect, useRef } from 'react';
import { useStore } from '@/store/store';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { getSkinnyBannerEventProps } from '@/tracking/amplitude/events/general/skinny-banner';
import { flushAmplitudeViaBeacon } from '@/tracking/amplitude/flush-beacon';
import type { SkinnyBannerType } from '@/types/zones/slices/skinny-banner-slice';

type SkinnyBannerLinkProps = {
    href?: string;
    bannerType?: SkinnyBannerType;
    target?: '_self' | '_blank';
    children: React.ReactNode;
};

const GAME_WINDOW_FEATURES = 'width=1400,height=900';

export const SkinnyBannerLink = ({ href, bannerType, target, children }: SkinnyBannerLinkProps) => {
    const { sendEvent } = useStore((state) => state.tracking);
    const addGameWindow = useStore((state) => state.addGameWindow);
    const [ref, isInView] = useIntersectionObserver<HTMLAnchorElement>({
        threshold: 0.5,
        rootMargin: '0px'
    });
    const fired = useRef(false);

    useEffect(() => {
        if (!isInView || !href || !bannerType || !sendEvent || fired.current) return;
        fired.current = true;
        const props = getSkinnyBannerEventProps({ bannerType, action: 'viewed', ctaUrl: href });
        if (props) {
            sendEvent([{ destination: 'amplitude', eventProps: props }]);
        }
    }, [isInView, href, bannerType, sendEvent]);

    if (!href) {
        return <div data-testid="skinny-banner-zone-wrapper">{children}</div>;
    }

    const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (sendEvent && bannerType) {
            const props = getSkinnyBannerEventProps({ bannerType, action: 'clicked', ctaUrl: href });
            if (props) {
                sendEvent([{ destination: 'amplitude', eventProps: props }]);
            }
        }

        if (target === '_blank') {
            event.preventDefault();
            const gameWindow = window.open(href, '_blank', GAME_WINDOW_FEATURES);
            if (gameWindow) {
                addGameWindow(gameWindow);
            }
            return;
        }

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
        }

        event.preventDefault();
        flushAmplitudeViaBeacon().finally(() => {
            window.location.href = href;
        });
    };

    return (
        <a
            ref={ref}
            className="skinny-banner-zone__link"
            href={href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            onClick={onClick}
            data-testid={`skinny-banner-${bannerType}-cta`}>
            {children}
        </a>
    );
};
